import React, { useEffect, useState, useRef, useCallback } from "react";

// --- Helper Functions (kept outside the component for clarity) ---
function ensureHttp(link) {
  link = link.trim();

  const div = document.createElement("div");
  div.appendChild(document.createTextNode(link));
  const safeLink = div.innerHTML;

  const lowerLink = safeLink.toLowerCase();
  if (
    lowerLink.startsWith("javascript:") ||
    lowerLink.startsWith("data:") ||
    lowerLink.startsWith("vbscript:")
  ) {
    return "#";
  }

  if (!lowerLink.startsWith("http://") && !lowerLink.startsWith("https://")) {
    return "http://" + safeLink;
  }

  return safeLink;
}

async function resolveRedirect(originalUrl) {
  try {
    const res = await fetch(
      `/php/resolve.php?url=${encodeURIComponent(originalUrl)}`
    );
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    return await res.text();
  } catch (error) {
    console.error("Error resolving redirect:", error);
    return originalUrl; // Fallback to original URL on error
  }
}

function getEmbedUrl(link) {
  try {
    const url = new URL(link);
    const hostname = url.hostname.toLowerCase();
    const pathname = url.pathname.toLowerCase();

    // ✅ 1. Direct video file support
    const videoExtensions = [".mp4", ".webm", ".ogg", ".mov", ".m4v"];
    const streamExtensions = [".m3u8"]; // HLS streams

    if (
      videoExtensions.some(ext => pathname.endsWith(ext)) ||
      streamExtensions.some(ext => pathname.endsWith(ext))
    ) {
      return {
        url: link,
        type: "direct", // render using <video>
      };
    }

    if (hostname.includes("youtube.com") || hostname.includes("youtu.be")) {
      let videoId;
      if (url.pathname.startsWith("/shorts/")) {
        videoId = url.pathname.split("/shorts/")[1]?.split("/")[0];
        return {
          url: `https://www.youtube-nocookie.com/embed/${videoId}`,
          type: "vertical",
        };
      } else if (hostname.includes("youtu.be")) {
        videoId = url.pathname.slice(1);
      } else {
        videoId = new URLSearchParams(url.search).get("v");
      }
      return {
        url: `https://www.youtube-nocookie.com/embed/${videoId}`,
        type: "normal",
      };
    }

    if (hostname.includes("vimeo.com")) {
      const videoId = url.pathname.split("/")[1];
      return {
        url: `https://player.vimeo.com/video/${videoId}`,
        type: "normal",
      };
    }

    if (hostname.includes("dailymotion.com") || hostname === "dai.ly") {
      const videoId =
        hostname === "dai.ly"
          ? url.pathname.slice(1)
          : url.pathname.split("/video/")[1];
      return {
        url: `https://www.dailymotion.com/embed/video/${videoId}`,
        type: "normal",
      };
    }

    if (hostname.includes("wistia.com") || hostname.includes("wistia.net")) {
      const match = link.match(/(medias|iframe)\/([a-zA-Z0-9]+)/);
      if (match && match[2]) {
        return {
          url: `https://fast.wistia.net/embed/iframe/${match[2]}`,
          type: "normal",
        };
      }
    }

    if (hostname.includes("tiktok.com")) {
      const match = link.match(/\/video\/(\d+)/);
      if (match && match[1]) {
        // TikTok embed doesn't officially support autoplay via URL params
        return {
          url: `https://www.tiktok.com/embed/v2/${match[1]}`,
          type: "vertical",
        };
      }
    }

    if (hostname.includes("loom.com")) {
      const match = link.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
      if (match && match[1]) {
        return {
          url: `https://www.loom.com/embed/${match[1]}`,
          type: "normal",
        };
      }
    }

    if (hostname.includes("twitch.tv")) {
      const match = link.match(/\/videos\/(\d+)/);
      if (match && match[1]) {
        return {
          url: `https://player.twitch.tv/?video=${match[1]}&parent=${window.location.hostname}`,
          type: "normal",
        };
      }
    }

    if (hostname.includes("vidyard.com")) {
      const match = link.match(/vidyard\.com\/watch\/([a-zA-Z0-9\-]+)/);
      if (match && match[1]) {
        // Vidyard autoplay param (if any) might vary, often &autoplay=true works
        return {
          url: `https://play.vidyard.com/${match[1]}.html`,
          type: "normal",
        };
      }
    }

    return null;
  } catch (error) {
    console.error("Error parsing embed URL:", error);
    return null;
  }
}

// --- ShowLinkModal Component ---
const ShowLinkModal = ({ visitLink, onClose }) => {
  const [embedInfo, setEmbedInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const modalRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const offset = useRef({ x: 0, y: 0 }); // Offset from modal's top-left to mouse click
  const [isMinimized, setIsMinimized] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  // New state to store the last dragged position
  const [lastDraggedPos, setLastDraggedPos] = useState(null); // {x: number, y: number}
const [finalPreviewUrl, setFinalPreviewUrl] = useState(null);
  // --- Dynamic Modal Dimensions ---
  const isVertical = embedInfo?.type === "vertical";
  const embedHeight =
    screenWidth < 601 && !isVertical ? 222 : isVertical ? 600 : 338;
  const modalHeight = isVertical ? 660 : 380;
  const defaultModalWidth = isVertical ? 400 : 600;
  const modalWidth =
    screenWidth < 601
      ? isVertical
        ? `${defaultModalWidth}px`
        : "100%"
      : `${defaultModalWidth}px`;

  // --- Dragging Logic ---
  const startDragging = useCallback((clientX, clientY) => {
    if (modalRef.current) {
      const rect = modalRef.current.getBoundingClientRect();
      offset.current = {
        x: clientX - rect.left,
        y: clientY - rect.top,
      };
      setIsDragging(true);

      // When dragging starts, fix modal position relative to viewport
      modalRef.current.style.position = "fixed";
      modalRef.current.style.top = `${rect.top}px`;
      modalRef.current.style.left = `${rect.left}px`;
      modalRef.current.style.transform = "none"; // Clear any transform centering
    }
  }, []);

  const handleMouseDown = useCallback(
    (e) => {
      if (e.target.dataset.dragHandle) {
        e.preventDefault();
        startDragging(e.clientX, e.clientY);
      }
    },
    [startDragging]
  );

  const handleTouchStart = useCallback(
    (e) => {
      if (e.target.dataset.dragHandle) {
        e.preventDefault();
        const touch = e.touches[0];
        startDragging(touch.clientX, touch.clientY);
      }
    },
    [startDragging]
  );

  const handleMove = useCallback(
    (clientX, clientY) => {
      if (!isDragging || !modalRef.current) return;

      const modal = modalRef.current;

      // Calculate new position relative to the viewport
      let newLeft = clientX - offset.current.x;
      let newTop = clientY - offset.current.y;

      // Constrain movement to viewport boundaries
      const modalRect = modal.getBoundingClientRect();
      const maxLeft = window.innerWidth - modalRect.width;
      const maxTop = window.innerHeight - modalRect.height;

      newLeft = Math.max(0, Math.min(newLeft, maxLeft));
      newTop = Math.max(0, Math.min(newTop, maxTop));

      modal.style.left = `${newLeft}px`;
      modal.style.top = `${newTop}px`;
      modal.style.transform = `translate(0, 0)`; // Ensure no transform interferes
    },
    [isDragging]
  );

  const handleMouseMove = useCallback(
    (e) => handleMove(e.clientX, e.clientY),
    [handleMove]
  );
  const handleTouchMove = useCallback(
    (e) => {
      const touch = e.touches[0];
      handleMove(touch.clientX, touch.clientY);
    },
    [handleMove]
  );

  const stopDragging = useCallback(() => {
    setIsDragging(false);
    if (modalRef.current) {
      // Store the current position when dragging stops
      const rect = modalRef.current.getBoundingClientRect();
      setLastDraggedPos({ x: rect.left, y: rect.top });
    }
  }, []);

  // --- Event Listener Setup for Dragging ---
  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", stopDragging);
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", stopDragging);
    window.addEventListener("touchcancel", stopDragging);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", stopDragging);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", stopDragging);
      window.removeEventListener("touchcancel", stopDragging);
    };
  }, [handleMouseMove, stopDragging, handleTouchMove]);

  // Prevent default scroll behavior on touchmove during drag and apply touch-action
  useEffect(() => {
    const preventScroll = (e) => {
      if (isDragging) {
        e.preventDefault();
      }
    };

    document.body.style.touchAction = isDragging ? "none" : "auto";
    document.addEventListener("touchmove", preventScroll, { passive: false });

    return () => {
      document.body.style.touchAction = "auto";
      document.removeEventListener("touchmove", preventScroll);
    };
  }, [isDragging]);

  // --- Positioning Logic (Centralized) ---
  const updateModalPosition = useCallback(() => {
    if (!modalRef.current) return;
    const modal = modalRef.current;

    if (isMinimized) {
      // Minimized to bottom-left corner of viewport
      modal.style.top = `${window.innerHeight - 115}px`;
      modal.style.left = `0px`;
      modal.style.transform = `translate(0, 0)`;
      modal.style.right = "auto"; // Ensure 'right' is not set
      modal.style.bottom = "auto"; // Ensure 'bottom' is not set
    } else {
      // Maximized: Use last dragged position if available, otherwise center
      if (lastDraggedPos) {
        modal.style.top = `${lastDraggedPos.y}px`;
        modal.style.left = `${lastDraggedPos.x}px`;
        modal.style.transform = `translate(0, 0)`; // Clear transform
        modal.style.right = "auto";
        modal.style.bottom = "auto";
      } else {
        // Default centering if no dragged position is set
        modal.style.top = `50%`;
        modal.style.left = `50%`;
        modal.style.transform = `translate(-50%, -50%)`;
        modal.style.right = "auto";
        modal.style.bottom = "auto";
      }
    }
  }, [isMinimized, lastDraggedPos]); // Added lastDraggedPos to dependencies

  // --- Minimize/Maximize Logic ---
  const handleMinimize = useCallback(() => {
    setIsMinimized((prevMinimized) => {
      const newMinimized = !prevMinimized;
      setIsDragging(false); // Stop dragging if minimizing/maximizing
      // When minimizing/maximizing, we want to reset the dragged position
      // so the next 'maximize' goes to center by default, unless dragged.
      setLastDraggedPos(null); // Clear last dragged position
      return newMinimized;
    });
  }, []);

  // --- Initial Position, Resize, and Scroll Handling ---
  useEffect(() => {
    // Initial positioning and update on isMinimized, lastDraggedPos, and screenWidth change
    updateModalPosition();

    const handleResizeOrScroll = () => {
      setScreenWidth(window.innerWidth); // Update screen width for dynamic sizing
      if (!isDragging) {
        // Only re-position if not currently being dragged
        updateModalPosition();
      }
    };

    window.addEventListener("resize", handleResizeOrScroll);
    window.addEventListener("scroll", handleResizeOrScroll);

    return () => {
      window.removeEventListener("resize", handleResizeOrScroll);
      window.removeEventListener("scroll", handleResizeOrScroll);
    };
  }, [isMinimized, isDragging, updateModalPosition, screenWidth]); // Added screenWidth to dependencies for dynamic sizing

  // --- Embed Info Fetching ---
  useEffect(() => {
  if (!visitLink) {
    setEmbedInfo(null);
    setFinalPreviewUrl(null);
    return;
  }

  const processLink = async () => {
    setLoading(true);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    let finalUrl = finalPreviewUrl || visitLink;

    // 🔥 Resolve email → username
    if (emailRegex.test(visitLink)) {
      try {
        const res = await fetch(`/php/getUsernameByEmail.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: visitLink }),
        });

        const data = await res.json();

        if (data.success && data.username) {
          finalUrl = `https://textalk.city/@${encodeURIComponent(data.username)}`;
        }
      } catch (e) {
        console.error(e);
      }
    }

    setFinalPreviewUrl(finalUrl);

    // Existing embed logic
    const safeLink = ensureHttp(finalUrl);

    let resolved = safeLink;
    try {
      const url = new URL(safeLink);
      if (url.hostname.includes("tiktok.com")) {
        resolved = await resolveRedirect(safeLink);
      }
    } catch {}

    setEmbedInfo(getEmbedUrl(resolved));
    setLoading(false);
  };

  processLink();
}, [visitLink]);

  if (!visitLink) return null;

  return (
    <div
      className="showLinkModal"
      ref={modalRef}
      style={{
        background: `linear-gradient(135deg, rgba(255,255,255,0.25), rgba(240,240,240,0.1)),
                 url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" fill-opacity="0.015"><rect width="100%" height="100%" fill="white"/></svg>')`,
        backgroundBlendMode: "overlay",
        backdropFilter: "blur(8px) brightness(1.05)",
        WebkitBackdropFilter: "blur(8px) brightness(1.05)",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        borderRadius: "12px",
        position: "fixed",
        right: "auto",
        bottom: "auto",

        width: isMinimized ? "200px" : modalWidth,
        height: isMinimized ? "150px" : "auto",
        maxHeight: isMinimized ? "114px" : modalHeight,
        minWidth: isMinimized ? "200px" : modalWidth,
        overflow: "hidden",

        transition: isDragging ? "none" : "width 0.3s ease, height 0.3s ease",
      }}
    >
      {/* Transparent overlay to capture mouse events over iframe when dragging */}
      {isDragging && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "transparent",
            zIndex: 999,
            cursor: "move",
          }}
        />
      )}

      {/* Control Buttons Container */}
      <div
        style={{
          width: "100%",
          height: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          margin: "4px",
        }}
      >
        {/* Minimize/Maximize Button (Left) */}
        <div
          onClick={handleMinimize}
          style={{
            fontSize: "25px",
            cursor: "pointer",
            userSelect: "none",
            marginLeft: "10px",
            transform: isMinimized ? "rotate(90deg)" : "none",
            transition: "transform 0.3s ease",
          }}
          title={isMinimized ? "Maximize" : "Minimize"}
        >
          {isMinimized ? "□" : "−"}
        </div>

        {/* Drag Icon (Center) */}
        <div
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          data-drag-handle="true"
          style={{
            fontSize: 20,
            cursor: "grab",
            userSelect: "none",
          }}
          title="Drag"
        >
          🟰
        </div>

        {/* Close Button (Right) */}
        <span
          onClick={onClose}
          style={{
            fontSize: 25,
            cursor: "pointer",
            marginRight: "15px",
          }}
          aria-label="Close modal"
        >
          ×
        </span>
      </div>

      {loading ? (
        <div
          style={{
            textAlign: "center",
            padding: 100,
            fontSize: 18,
            display: isMinimized ? "none" : "block",
          }}
        >
          Loading video...
        </div>
      ) : embedInfo?.url ? (
      <div
        style={{
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {embedInfo.type === "direct" ? (
          <video
            src={embedInfo.url}
            controls
            autoPlay
            playsInline
            width="100%"
            height={isMinimized ? "100%" : embedHeight}
            style={{
              border: "none",
              pointerEvents: isDragging ? "none" : "auto",
              borderRadius: "8px",
              background: "#000",
            }}
          />
        ) : (
          <iframe
            src={embedInfo.url}
            className="embedded-video"
            width="100%"
            height={isMinimized ? "100%" : embedHeight}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            title="Embedded Video"
            style={{
              border: "none",
              pointerEvents: isDragging ? "none" : "auto",
            }}
          />
        )}
      </div>
      ) : (
      <div style={{ display: isMinimized ? "none" : "block" }}>
        <br />
        <p style={{ marginTop: "-10px", textAlign: "center" }}>
          <span style={{ fontSize: "40px" }}>⚠️</span>
          <br />
          <br />
          Are you sure you want to visit this link?
        </p>
<div style={{ margin: "20px", textAlign: "center" }}>
  <div style={{ color: "#999", fontSize: "12px" }}>Redirecting to:</div>
  <div style={{ color: "red", wordBreak: "break-all", fontWeight: "bold" }}>
    {finalPreviewUrl || visitLink}
  </div>
</div>
        <br />
        <button
          onClick={async () => {
  onClose();

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  let finalUrl = visitLink;

  if (emailRegex.test(visitLink)) {
    try {
      const res = await fetch(`/php/getUsernameByEmail.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: visitLink }),
      });

      const data = await res.json();

      if (data.success && data.username) {
        // 🔥 FORCE TEXTALK DOMAIN
        finalUrl = `https://textalk.city/@${encodeURIComponent(data.username)}`;
      }
    } catch (e) {
      console.error("Redirect lookup failed:", e);
    }
  }

  setTimeout(() => {
    window.open(
      ensureHttp(finalUrl),
      "_blank",
      "noopener,noreferrer"
    );
  }, 100);
}}
          className="openLink"
          style={{ marginTop: "20px" }}
        >
          Visit in new tab
        </button>
      </div>
      )}
    </div>
  );
};

export default ShowLinkModal;
